import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SwalService } from '../../service/swal.service';
import {
  SubscriptionService,
  SubscriptionStatus,
} from '../../service/subscription.service';
import { IconComponent } from '../../components/icon/icon.component';

interface PlanCard {
  key: string;
  label: string;
  price: string;
  features: string[];
  highlight?: boolean;
}

@Component({
  selector: 'app-abbonamenti',
  imports: [CommonModule, TranslatePipe, IconComponent],
  templateUrl: './abbonamenti.component.html',
  styleUrl: './abbonamenti.component.css',
})
export class AbbonamentiComponent implements OnInit {
  private swal = inject(SwalService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private subscription = inject(SubscriptionService);
  private translate = inject(TranslateService);

  @ViewChild('planGrid') planGrid?: ElementRef<HTMLElement>;

  sub: SubscriptionStatus | null = null;
  loadingSub = true;
  processing: string | null = null;

  readonly plans: PlanCard[] = [
    {
      key: 'base',
      label: 'Base',
      price: '9,99',
      features: ['unlimited_events', 'pdf_contracts', 'price_list'],
    },
    {
      key: 'pro',
      label: 'Pro',
      price: '19,99',
      features: ['unlimited_events', 'pdf_contracts', 'price_list', 'maps_travel', 'stats', 'contract_themes'],
      highlight: true,
    },
    {
      key: 'agency',
      label: 'Agency',
      price: '29,99',
      features: [
        'unlimited_events',
        'pdf_contracts',
        'price_list',
        'maps_travel',
        'stats',
        'contract_themes',
        'team',
        'priority_support',
      ],
    },
  ];

  ngOnInit(): void {
    this.loadSubscription();
    this.handleCheckoutReturn();
  }

  private loadSubscription(): void {
    this.loadingSub = true;
    this.subscription.getStatus(
      (s) => {
        this.sub = s;
        this.loadingSub = false;
        setTimeout(() => this.centerHighlightedPlan());
      },
      () => {
        this.loadingSub = false;
      },
    );
  }

  // Su smartphone i piani sono un carosello: si parte dal consigliato
  private centerHighlightedPlan(): void {
    const grid = this.planGrid?.nativeElement;
    const card = grid?.querySelector<HTMLElement>('.plan-card.highlight');
    if (!grid || !card || grid.scrollWidth <= grid.clientWidth) return;
    grid.scrollLeft = card.offsetLeft - (grid.clientWidth - card.clientWidth) / 2;
  }

  private t(key: string, params?: any): string {
    return this.translate.instant(`subscription.${key}`, params);
  }

  private handleCheckoutReturn(): void {
    const checkout = this.route.snapshot.queryParamMap.get('checkout');
    if (!checkout) return;
    if (checkout === 'success') {
      this.swal.success(this.t('checkout_success'));
      setTimeout(() => this.loadSubscription(), 1500);
    } else if (checkout === 'cancel') {
      this.swal.errorDialog(this.t('checkout_cancel_title'), this.t('checkout_cancel_text'));
    }
    this.router.navigate([], { queryParams: {}, replaceUrl: true });
  }

  // ── Piano corrente ──
  get isSubscribed(): boolean {
    return this.sub?.plan !== 'free' && this.sub?.status === 'active';
  }

  isCurrentPlan(key: string): boolean {
    return this.isSubscribed && this.sub?.plan === key;
  }

  planLabel(): string {
    const map: Record<string, string> = {
      free: 'Free',
      base: 'Base',
      pro: 'Pro',
      agency: 'Agency',
    };
    return map[this.sub?.plan || 'free'] || 'Free';
  }

  statusLabel(): string {
    const status = this.sub?.status || 'trial';
    return ['trial', 'active', 'inactive', 'cancelled'].includes(status) ? this.t(`status.${status}`) : status;
  }

  get checkoutEnabled(): boolean {
    return !!this.sub?.checkoutEnabled;
  }

  // ── Azioni ──
  choosePlan(key: string): void {
    if (this.isCurrentPlan(key)) return;
    if (this.checkoutEnabled) {
      this.processing = key;
      this.subscription.startCheckout(key, () => {
        this.processing = null;
        this.swal.errorDialog(this.translate.instant('swal.error'), this.t('checkout_error'));
      });
    } else {
      this.requestPlan(key);
    }
  }

  private requestPlan(key: string): void {
    const label = this.plans.find((p) => p.key === key)?.label ?? key;
    this.swal
      .requestPrompt(
        this.t('request_title', { plan: label }),
        this.t('request_text'),
      )
      .then((res: any) => {
        if (!res?.isConfirmed) return;
        this.processing = key;
        this.subscription.requestPlan(
          key,
          res.value || '',
          () => {
            this.processing = null;
            this.swal.success(this.t('request_sent'));
          },
          () => {
            this.processing = null;
            this.swal.errorDialog(this.translate.instant('swal.error'), this.t('request_error'));
          },
        );
      });
  }

  cancelSubscription(): void {
    this.swal
      .confirm(
        this.t('cancel_title'),
        this.t('cancel_text'),
        this.t('cancel_confirm'),
        this.translate.instant('common.cancel'),
      )
      .then((res: any) => {
        if (!res?.isConfirmed) return;
        this.processing = 'cancel';
        this.subscription.cancel(
          () => {
            this.processing = null;
            this.swal.success(this.t('cancel_done'));
            this.loadSubscription();
          },
          () => {
            this.processing = null;
            this.swal.errorDialog(this.translate.instant('swal.error'), this.t('cancel_error'));
          },
        );
      });
  }

  reactivateSubscription(): void {
    this.processing = 'reactivate';
    this.subscription.reactivate(
      () => {
        this.processing = null;
        this.swal.success(this.t('reactivate_done'));
        this.loadSubscription();
      },
      () => {
        this.processing = null;
        this.swal.errorDialog(this.translate.instant('swal.error'), this.t('reactivate_error'));
      },
    );
  }
}
